import React, { useState, useEffect } from 'react';
import Banner from '../../components/Banner';
import { Link } from 'react-router-dom';
import { API_URL } from 'config';

interface BannerType {
  _id: string;
  imageUrl: string;
  order: number;
}

const HomePage = () => {
  const [banners, setBanners] = useState<BannerType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const response = await fetch(`${API_URL}/api/banners`);
        if (!response.ok) {
          throw new Error('Failed to fetch banners');
        }
        const data = await response.json();
        setBanners(data);
        setError(false);
      } catch (error) {
        console.error('Error fetching banners:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
  }, []);

  const LoadingSkeleton = () => (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <div className="relative h-[240px] md:h-[300px] overflow-hidden rounded-3xl bg-slate-200/80 animate-pulse" />
    </div>
  );

  return (
    <div className="pb-8">
      {(loading || error) ? <LoadingSkeleton /> : <Banner banners={banners} />}

      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-3xl p-8 md:p-12 bg-gradient-to-br from-white/95 via-white/90 to-blue-50/85 border border-white/70 shadow-[0_26px_50px_-30px_rgba(30,64,175,0.55)] overflow-hidden">
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-secondary/35 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-primary/20 rounded-full blur-3xl" />
          <div className="relative max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 text-primary text-sm font-semibold px-4 py-1.5 mb-5">
              Надежный поставщик инженерного оборудования
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 mb-6 leading-tight tracking-tight">
              Комплексные решения для отопления и водоснабжения
            </h1>
            <div className="space-y-4 text-lg md:text-xl text-slate-700 max-w-3xl">
              <p className="font-semibold text-primary-dark">
                Поставка котлов, насосов, котельных, радиаторов и запорной арматуры по лучшим ценам.
              </p>
              <p>
                Комплектация строительно-монтажных организаций и направление капитального ремонта по всей России.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/catalog"
                className="btn-primary"
              >
                Каталог оборудования
              </Link>
              <Link
                to="/callback"
                className="btn-secondary"
              >
                Получить консультацию
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="bg-white/85 border border-white rounded-2xl px-4 py-3">
                <p className="text-2xl font-extrabold text-primary">1000+</p>
                <p className="text-sm text-slate-600">позиций оборудования</p>
              </div>
              <div className="bg-white/85 border border-white rounded-2xl px-4 py-3">
                <p className="text-2xl font-extrabold text-primary">15+ лет</p>
                <p className="text-sm text-slate-600">работы в отрасли</p>
              </div>
              <div className="bg-white/85 border border-white rounded-2xl px-4 py-3">
                <p className="text-2xl font-extrabold text-primary">24/7</p>
                <p className="text-sm text-slate-600">поддержка клиентов</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="section-title mb-3">Почему выбирают Теплогранд</h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Делаем процесс закупки инженерного оборудования простым и предсказуемым: от подбора до поставки.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card group hover:bg-gradient-to-br hover:from-primary hover:to-accent-blue hover:text-white">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/15 group-hover:text-secondary-light mb-5">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3L2 12H5V20H19V12H22L12 3M12 8.5C14.21 8.5 16 10.29 16 12.5C16 14.71 14.21 16.5 12 16.5C9.79 16.5 8 14.71 8 12.5C8 10.29 9.79 8.5 12 8.5Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Широкий ассортимент</h3>
              <p className="text-slate-600 group-hover:text-white/90">Все необходимое оборудование для систем отопления и водоснабжения</p>
            </div>

            <div className="card group hover:bg-gradient-to-br hover:from-primary hover:to-accent-blue hover:text-white">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/15 group-hover:text-secondary-light mb-5">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2M12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20M13 7H11V13H17V11H13V7Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Быстрая доставка</h3>
              <p className="text-slate-600 group-hover:text-white/90">Оперативная доставка по всей России</p>
            </div>

            <div className="card group hover:bg-gradient-to-br hover:from-primary hover:to-accent-blue hover:text-white">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary group-hover:bg-white/15 group-hover:text-secondary-light mb-5">
                <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3C7.03 3 3 7.03 3 12C3 16.97 7.03 21 12 21C16.97 21 21 16.97 21 12C21 7.03 16.97 3 12 3M12 19C8.13 19 5 15.87 5 12C5 8.13 8.13 5 12 5C15.87 5 19 8.13 19 12C19 15.87 15.87 19 12 19M15.04 10.54L13.15 8.65L10.93 10.88L9.21 9.16L7.46 10.91L10.93 14.38L15.04 10.54Z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold mb-2">Гарантия качества</h3>
              <p className="text-slate-600 group-hover:text-white/90">Только проверенное оборудование от надежных производителей</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 